'use client'

import dynamic from 'next/dynamic'
import { useMemo } from 'react'

import type { CalInlineBlock as CalInlineBlockProps } from '@/payload-types'

import { SectionWrapper } from '@/components/SectionWrapper'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import SectionHeader from '@/components/ui/SectionHeader'

const Cal = dynamic(() => import('@calcom/embed-react').then(mod => mod.default), {
  ssr: false,
})

function normalizeBookingUrl(url: string): {
  calLink: string
  host: 'cal.com' | 'cal.eu'
  calOrigin: 'https://app.cal.com' | 'https://app.cal.eu'
} {
  const trimmedUrl = url.trim()

  // If it's already a full URL, extract host + path
  if (trimmedUrl.startsWith('https://cal.com/') || trimmedUrl.startsWith('http://cal.com/')) {
    return {
      calLink: trimmedUrl.replace(/^https?:\/\/cal\.com\//, ''),
      host: 'cal.com',
      calOrigin: 'https://app.cal.com',
    }
  }

  if (trimmedUrl.startsWith('https://app.cal.com/') || trimmedUrl.startsWith('http://app.cal.com/')) {
    return {
      calLink: trimmedUrl.replace(/^https?:\/\/app\.cal\.com\//, ''),
      host: 'cal.com',
      calOrigin: 'https://app.cal.com',
    }
  }

  if (trimmedUrl.startsWith('https://cal.eu/') || trimmedUrl.startsWith('http://cal.eu/')) {
    return {
      calLink: trimmedUrl.replace(/^https?:\/\/cal\.eu\//, ''),
      host: 'cal.eu',
      calOrigin: 'https://app.cal.eu',
    }
  }

  if (trimmedUrl.startsWith('https://app.cal.eu/') || trimmedUrl.startsWith('http://app.cal.eu/')) {
    return {
      calLink: trimmedUrl.replace(/^https?:\/\/app\.cal\.eu\//, ''),
      host: 'cal.eu',
      calOrigin: 'https://app.cal.eu',
    }
  }

  // Remove a leading slash from short links to keep the expected username/event format
  if (trimmedUrl.startsWith('/')) {
    return {
      calLink: trimmedUrl.slice(1),
      host: 'cal.com',
      calOrigin: 'https://app.cal.com',
    }
  }

  // Otherwise assume it's already in short format (username/event)
  return {
    calLink: trimmedUrl,
    host: 'cal.com',
    calOrigin: 'https://app.cal.com',
  }
}

type CalConfigValue = string | string[] | Record<string, string>
type CalConfig = Record<string, CalConfigValue>

function toCalConfig(config: unknown): CalConfig | undefined {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return undefined
  }

  return config as CalConfig
}

export const CalInlineBlockClient: React.FC<CalInlineBlockProps> = ({
  infoTagText,
  bookingUrl,
  title,
  description,
  namespace,
  config,
  settings,
}) => {
  const normalizedSettings = normalizeSectionSettings(settings)

  const bookingConfig = useMemo(
    () => (bookingUrl ? normalizeBookingUrl(bookingUrl) : null),
    [bookingUrl],
  )
  const normalizedUrl = bookingConfig?.calLink ?? ''
  const bookingHost = bookingConfig?.host ?? 'cal.com'
  const calOrigin = bookingConfig?.calOrigin ?? 'https://app.cal.com'
  const calNamespace = namespace || 'cal-inline'
  const fallbackUrl = normalizedUrl ? `https://${bookingHost}/${normalizedUrl}` : undefined

  // Cal.com expects: Record<string, string | string[] | Record<string, string>>
  // Default to Europe/Zurich (Swiss company); admin can override via the config JSON.
  const calConfig = useMemo(
    () => ({ timeZone: 'Europe/Zurich', ...(toCalConfig(config) ?? {}) }),
    [config],
  )

  if (!normalizedUrl) {
    return null
  }

  return (
    <SectionWrapper settings={normalizedSettings}>
      <MotionFadeIn className="w-full">
        {(infoTagText || title || description) && (
          <div className="mb-8">
            <SectionHeader
              infoTagText={infoTagText || ''}
              title={title || undefined}
              subtitle={description || undefined}
              className="mb-0"
            />
          </div>
        )}

        <div className="w-full overflow-hidden rounded-lg" style={{ minHeight: '600px' }}>
          <Cal
            namespace={calNamespace}
            calLink={normalizedUrl}
            calOrigin={calOrigin}
            style={{ width: '100%', height: '100%', overflow: 'scroll' }}
            {...(calConfig ? { config: calConfig } : {})}
          />
        </div>
        {fallbackUrl && (
          <p className="mt-4 text-sm text-text-secondary">
            Falls das Kalender-Widget nicht geladen wird, nutzen Sie bitte{' '}
            <a
              className="underline underline-offset-2"
              href={fallbackUrl}
              target="_blank"
              rel="noreferrer"
            >
              diesen direkten Buchungslink
            </a>
            .
          </p>
        )}
      </MotionFadeIn>
    </SectionWrapper>
  )
}
